import styled from '@emotion/styled';
import { initEcharts } from 'lib/echarts';
import { HTMLAttributes, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import dayjs from 'lib/dayjs';
import { useSize } from 'ahooks';
import { useTranslate } from '@tolgee/react';

const Container = styled.div`
  height: 250px;
`;

const splitNumber = 5;

function getChartMaxValue(data: number[], splitNumber: number) {
  const max = Math.max(...data);

  if (!max) {
    return 100;
  }

  return Math.ceil(max / splitNumber) * splitNumber;
}

export interface OrderChartProps extends HTMLAttributes<HTMLDivElement> {
  loading?: boolean;
  data?: Incentive.OrderData[];
}

function OrderChart(props: OrderChartProps) {
  const { loading, data, ...otherProps } = props;

  const { t } = useTranslate();

  const option = useMemo(() => {
    return {
      color: ['#989AFF'],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: '#999',
          },
        },
      },
      grid: {
        containLabel: true,
        top: 15,
        left: 0,
        right: 0,
        bottom: 0,
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true,
          },
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          position: 'left',
        },
      ],
      series: [
        {
          name: t('incentive.店铺付款订单量'),
          type: 'bar',
        },
      ],
    };
  }, [t]);

  const updateOption = useMemo(() => {
    const countData = (data || []).map((_) => _.orderCount);

    const chartMaxValue = getChartMaxValue(countData, splitNumber);

    return {
      xAxis: [
        {
          type: 'category',
          axisTick: {
            alignWithLabel: true,
          },
          data: (data || []).map(({ date }) => dayjs(date).format('MM.DD')),
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          position: 'left',
          max: chartMaxValue,
          splitNumber,
          interval: chartMaxValue / splitNumber,
        },
      ],
      series: [
        {
          name: t('incentive.店铺付款订单量'),
          type: 'bar',
          data: countData,
          barMaxWidth: 20,
        },
      ],
    };
  }, [data, t]);

  const containerRef = useRef<HTMLDivElement>(null);

  const { width, height } = useSize(containerRef) || {};

  const chartRef = useRef<echarts.ECharts>();

  const refsCurrent = { option };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const { current: container } = containerRef;

    if (!container) {
      return;
    }

    const {
      current: { option },
    } = refs;

    const chart = initEcharts(container);
    chartRef.current = chart;
    chart.setOption(option);
  }, []);

  useLayoutEffect(() => {
    const { current: chart } = chartRef;

    if (!chart) {
      return;
    }

    chart.resize();
  }, [width, height]);

  useEffect(() => {
    const { current: chart } = chartRef;

    if (!chart) {
      return;
    }

    chart.setOption(updateOption);
  }, [updateOption]);

  useEffect(() => {
    const { current: chart } = chartRef;

    if (!chart) {
      return;
    }

    if (loading) {
      chart.showLoading();
    } else {
      chart.hideLoading();
    }
  }, [loading]);

  return <Container ref={containerRef} {...otherProps} />;
}

export default OrderChart;
